import {Map, RecordOf} from 'immutable';
import {getInPojo, getString} from '@packages/utilities/data-utils';
import {getElse} from '@packages/systems/core/utils/ImmUtils';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {StyleAttributeImm} from '@packages/systems/wfdl';
import {removeCollectionSlugFromUrlPath} from '@packages/systems/core/utils/UrlUtils';
import {getCollectionSlug} from '@packages/systems/dynamo/utils/CollectionUtils';
import getActiveLocale from './utils/getActiveLocale';
import {EvaluatedData, ExpansionProps} from '../types';
import {RenderContext} from '../RenderContext';
import {LocaleItemContext} from '@packages/shared/constants/Render';

// Collection template page links in Rich Text have their `href` attribute with
// a hard-coded collection slug from the time the link was first created. Since
// then the Collection slug may have renamed, so we are deriving the correct slug
// using the Collection ID that is also stored in the Link node data.
export function replaceRichTextCollectionTemplatePageHrefs<
  T extends ExpansionProps,
>(
  props: T,
  collectionIdToPageIdMap: RenderContext['collectionIdToPageIdMap'],
  runtimeEnvironment: Environment
): T {
  const {
    data,
    attributes,
    pluginsContext,
    sharedContext: {hrefMap},
  } = props;

  const collectionId = getLinkCollectionId(data);

  if (
    collectionId &&
    isLinkNeedingCollectionSlugReplacement(data, runtimeEnvironment)
  ) {
    return {
      ...props,
      attributes: attributes.update(
        'href',
        replaceCollectionSlug(
          collectionId,
          getDynamoCollections(pluginsContext),
          collectionIdToPageIdMap,
          hrefMap,
          getActiveLocale(pluginsContext)
        )
      ),
    };
  }

  return props;
}

const getLinkMode = (data: EvaluatedData) =>
  getString(getInPojo(data, ['link', 'mode']));
const getLinkItemId = (data: EvaluatedData) =>
  getString(getInPojo(data, ['link', 'itemId']));
const getLinkCollectionId = (data: EvaluatedData) =>
  getString(getInPojo(data, ['link', 'collectionId']));
const getDynamoCollections = getElse(['Dynamo', 'collections'], Map());

function isLinkNeedingCollectionSlugReplacement(
  data: EvaluatedData,
  runtimeEnvironment: null | Environment
): boolean {
  return (
    runtimeEnvironment !== WebflowEnvironments.EXPORT &&
    getLinkMode(data) === 'page' &&
    Boolean(getLinkItemId(data) && getLinkCollectionId(data))
  );
}

/**
 * Prepends the current locale's subdirectory to the resolved href when the
 * href does not already include it. The server-side `hrefMap` embedded in the
 * page JSON is built from the primary locale, so without this step Rich Text
 * links to CMS detail pages on secondary locales lose their locale prefix
 * (e.g. `/post/translated-slug` instead of `/es/post/translated-slug`).
 * See CONT-6234 / LOCAL-77.
 *
 * Callers must only pass site-root-relative paths. The `replaceCollectionSlug`
 * closure already short-circuits on non-root-relative hrefs so that the
 * `pathToPage` concatenation upstream cannot produce a malformed value like
 * `/bloghttps://example.com/...`; the guard here is a belt-and-braces check
 * that returns absolute (`https://example.com/...`), protocol-relative
 * (`//host/x`), and non-path (`mailto:`, `tel:`, empty) strings unchanged.
 */
function prependLocaleSubdirectory(
  href: string,
  currentLocale: LocaleItemContext | null
): string {
  const subdirectory = currentLocale?.subdirectory;
  if (!subdirectory) {
    return href;
  }
  // Belt-and-braces: only site-root-relative paths (`/foo`) are safe to
  // prefix. `replaceCollectionSlug` already rejects everything else up front,
  // so this branch should not be reachable in practice.
  if (!href.startsWith('/') || href.startsWith('//')) {
    return href;
  }
  const localePrefix = `/${subdirectory}`;
  if (href === localePrefix || href.startsWith(`${localePrefix}/`)) {
    return href;
  }
  return `${localePrefix}${href}`;
}

const replaceCollectionSlug =
  (
    collectionId: string,
    collections: Map<string, any> | RecordOf<any>,
    collectionIdToPageIdMap: {[key: string]: string},
    hrefMap: {[key: string]: string},
    currentLocale: LocaleItemContext | null
  ) =>
  (href: string | StyleAttributeImm | Map<string, string>) => {
    if (!href) {
      return href;
    }
    // Non-root-relative hrefs (absolute `https://...`, protocol-relative
    // `//host/x`, `mailto:`, `tel:`, ...) are never site paths. Page-mode
    // links on RichText fields go through `setLinkDataFromAttributes` which
    // stores the anchor href as-is on the `dynpage` branch, so an
    // author-supplied absolute or malformed href can reach this transform.
    // The slug-rewrite regex requires a leading `/` and would leave those
    // hrefs unchanged, but the `pathToPage` concatenation below would corrupt
    // them (e.g. `/blog` + `https://example.com/x` -> `/bloghttps://...`).
    // Bypass the whole rewrite here so those hrefs pass through untouched.
    if (
      typeof href === 'string' &&
      (!href.startsWith('/') || href.startsWith('//'))
    ) {
      return href;
    }
    const collection = collections.get(collectionId);
    const collectionSlug = collection
      ? getCollectionSlug(
          typeof collection.toJS === 'function'
            ? collection.toJS()
            : collection,
          currentLocale?.cmsId ?? undefined
        )
      : null;

    const newHref =
      collectionSlug && typeof href === 'string'
        ? href.replace(/(^\/)[^/]+(\/.+)/, `$1${collectionSlug}$2`)
        : href;

    // This logic is for handling links to template pages that are nested in folders.
    // For example `/somefolder/someCollection/slug-here
    const pageId = collectionIdToPageIdMap[collectionId];
    // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type.
    let pathToPage = hrefMap[pageId];
    if (pathToPage) {
      pathToPage = removeCollectionSlugFromUrlPath(pathToPage);

      const resolved =
        typeof newHref === 'string' ? `${pathToPage}${newHref}` : newHref;

      return typeof resolved === 'string'
        ? prependLocaleSubdirectory(resolved, currentLocale)
        : resolved;
    }

    return typeof newHref === 'string'
      ? prependLocaleSubdirectory(newHref, currentLocale)
      : newHref;
  };
